/**
 * Global coordinator for game catalog icon loads (PLA-466).
 *
 * Phase A: visible-tier bulk grants, near/rest throttled, URL dedupe, scroll promotion.
 */

export type IconLoadTier = "visible" | "near" | "rest";

export const ICON_LOAD_REST_MAX_CONCURRENT = 4;

/** 1×1 transparent GIF — holds layout without hitting the CDN until load is granted. */
export const ICON_LOAD_PLACEHOLDER_SRC =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

export function isIconLoadPlaceholderSrc(src: string | null | undefined): boolean {
  return src === ICON_LOAD_PLACEHOLDER_SRC;
}

type LoadEntry = {
  id: string;
  imageUrl: string;
  tier: IconLoadTier;
  priority: boolean;
  granted: boolean;
  usesRestSlot: boolean;
  slotReleased: boolean;
  onGrant: () => void;
};

const entries = new Map<string, LoadEntry>();
const restQueue: string[] = [];
const urlLoaded = new Set<string>();
const urlInFlight = new Set<string>();
const urlWaiters = new Map<string, Set<() => void>>();

let restInFlight = 0;

function notifyUrlWaiters(imageUrl: string): void {
  const waiters = urlWaiters.get(imageUrl);
  if (!waiters) return;
  urlWaiters.delete(imageUrl);
  for (const notify of waiters) {
    notify();
  }
}

function grantEntry(entry: LoadEntry, usesRestSlot: boolean): void {
  if (entry.granted) return;
  entry.granted = true;
  entry.usesRestSlot = usesRestSlot;
  if (usesRestSlot) {
    restInFlight += 1;
  }
  if (!urlLoaded.has(entry.imageUrl)) {
    urlInFlight.add(entry.imageUrl);
  }
  entry.onGrant();
}

function enqueueRest(entry: LoadEntry): void {
  if (entry.granted) return;
  if (!restQueue.includes(entry.id)) {
    restQueue.push(entry.id);
  }
  drainRestQueue();
}

function drainRestQueue(): void {
  while (restInFlight < ICON_LOAD_REST_MAX_CONCURRENT && restQueue.length > 0) {
    const id = restQueue.shift();
    if (!id) break;
    const entry = entries.get(id);
    if (!entry || entry.granted) continue;
    grantEntry(entry, true);
  }
}

function removeFromRestQueue(id: string): void {
  const index = restQueue.indexOf(id);
  if (index >= 0) {
    restQueue.splice(index, 1);
  }
}

function waitForUrl(imageUrl: string, onReady: () => void): void {
  let waiters = urlWaiters.get(imageUrl);
  if (!waiters) {
    waiters = new Set();
    urlWaiters.set(imageUrl, waiters);
  }
  waiters.add(onReady);
}

function scheduleGrant(entry: LoadEntry): void {
  if (urlLoaded.has(entry.imageUrl)) {
    grantEntry(entry, false);
    return;
  }

  if (urlInFlight.has(entry.imageUrl)) {
    waitForUrl(entry.imageUrl, () => {
      if (entries.has(entry.id) && !entry.granted) {
        grantEntry(entry, false);
      }
    });
    return;
  }

  if (entry.priority || entry.tier === "visible") {
    grantEntry(entry, false);
    return;
  }

  enqueueRest(entry);
}

export function registerIconLoadRequest(options: {
  id: string;
  imageUrl: string;
  tier: IconLoadTier;
  priority: boolean;
  onGrant: () => void;
}): () => void {
  const entry: LoadEntry = {
    id: options.id,
    imageUrl: options.imageUrl,
    tier: options.tier,
    priority: options.priority,
    granted: false,
    usesRestSlot: false,
    slotReleased: false,
    onGrant: options.onGrant,
  };

  entries.set(options.id, entry);

  if (options.tier === "rest") {
    return () => {
      removeFromRestQueue(options.id);
      entries.delete(options.id);
    };
  }

  scheduleGrant(entry);

  return () => {
    if (entry.granted && !entry.slotReleased) {
      completeIconLoadSlot(options.id, entry.imageUrl, false);
    }
    removeFromRestQueue(options.id);
    entries.delete(options.id);
  };
}

/** Promote tier on scroll; visible icons jump the line. */
export function updateIconLoadTier(id: string, tier: IconLoadTier, priority: boolean): void {
  const entry = entries.get(id);
  if (!entry || entry.granted) return;

  entry.tier = tier;
  entry.priority = priority;

  if (tier === "rest") return;

  removeFromRestQueue(id);
  scheduleGrant(entry);
}

export function completeIconLoadSlot(id: string, imageUrl: string, succeeded: boolean): void {
  const entry = entries.get(id);
  if (!entry || entry.slotReleased) return;
  entry.slotReleased = true;

  if (entry.usesRestSlot) {
    restInFlight = Math.max(0, restInFlight - 1);
    drainRestQueue();
  }

  if (succeeded) {
    urlLoaded.add(imageUrl);
  }
  urlInFlight.delete(imageUrl);
  notifyUrlWaiters(imageUrl);
}

export function isIconUrlCached(imageUrl: string): boolean {
  return urlLoaded.has(imageUrl);
}

/** @internal test helper */
export function resetIconLoadCoordinatorForTests(): void {
  entries.clear();
  restQueue.length = 0;
  urlLoaded.clear();
  urlInFlight.clear();
  urlWaiters.clear();
  restInFlight = 0;
}

/** @internal test helper */
export function getIconLoadCoordinatorStatsForTests(): {
  restInFlight: number;
  restQueueLength: number;
} {
  return { restInFlight, restQueueLength: restQueue.length };
}
